import React, { useEffect, useState } from "react";
import Navbar from "../../../components/Navbar";
import Footer from "../../../components/Footer";
import CartItem from "../../../helper/images/menuimage.png";
import { MdStar } from "react-icons/md";
import { useLocation } from "react-router-dom";
import { placeOrder, getAvailableOffers } from "../../../api/user/restaurants";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { checkUserLocation } from "../../../utils/location";
import toast from "react-hot-toast";
import { launchConfetti } from "../../../helper/partyEffect";
import { useNavigate } from "react-router-dom";
import {
  getCartPrice,
  addOrUpdateCartItem,
  removeCartItem,
  getCart,
} from "../../../api/user/restaurants/cart";
import { useTranslation } from "../../../hooks/useTranslation";
import { ClipLoader } from "react-spinners";
import { useLocationData } from "../../../context/locationContext";
import { CartLoading } from "../../../helper/lorder";

const PopupModal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed sm:inset-0 bottom-0 left-0 right-0 bg-black bg-opacity-50 flex sm:items-center items-end justify-center z-50 h-full">
      <div className="bg-white sm:rounded-[20px] rounded-t-[20px] w-full sm:w-[845px] relative sm:shadow-lg">
        <button
          onClick={onClose}
          className="absolute top-[33px] right-[30px] text-[#9E9E9E] hover:text-black"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
};

const CartStep = () => {
  const { userLocation, setUserLocation } = useLocationData();
  const [loadingItems, setLoadingItems] = useState({});
  const [loadingAction, setLoadingAction] = useState({}); // Track which action is loading: 'add' or 'remove'
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);
  const [isDeliveryModalOpen, setIsDeliveryModalOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [tableNumber, setTableNumber] = useState("");
  const [deliveryType, setDeliveryType] = useState(""); // "delivery" or "pickup"
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [locationStatus, setLocationStatus] = useState(null);
  const [locationError, setLocationError] = useState("");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { reservationId } = location.state || {};

  const { data: cartData, isLoading: cartLoading } = useQuery({
    queryKey: ["cart"],
    queryFn: () => getCart(),
  });

  const restaurantId = cartData?.data?.restaurantId || null;
  // const reservationId = cartData?.data?.reservationId?._id || null;
  const cart = cartData?.data?.items || [];

  const [debouncedCart, setDebouncedCart] = useState(cart);

  const { data, isLoading:cardLoadingPrice } = useQuery({
    queryKey: ["cart-price", debouncedCart, selectedOffer],
    queryFn: () => getCartPrice({ offerId: selectedOffer?._id }),
    enabled: !!cart.length,
    keepPreviousData: true,
  });

  const { data: offersData, isLoading: offersLoading } = useQuery({
    queryKey: ["offers", restaurantId, debouncedCart],
    queryFn: () => getAvailableOffers({ restaurantId }),
    enabled: !!restaurantId,
    keepPreviousData: true,
  });

  const offers = offersData?.data?.offers || [];

  const bookingMutation = useMutation({
    mutationFn: (data) => placeOrder(data),
    onSuccess: ({ data }) => {
      const { url } = data;
      if (url) {
        window.open(url, "_blank");
      } else {
        toast.error("Checkout URL not found.");
      }
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to get checkout url.";
      toast.error(errorMessage);
    },
  });

  const charges = data?.data?.charges;

  const addOrUpdateMutation = useMutation({
    mutationFn: addOrUpdateCartItem,
    onSuccess: () => {
      queryClient.invalidateQueries(["cart"]);
      queryClient.invalidateQueries(["cart-price"]);
    },
  });

  const removeMutation = useMutation({
    mutationFn: removeCartItem,
    onSuccess: () => {
      queryClient.invalidateQueries(["cart"]);
      queryClient.invalidateQueries(["cart-price"]);
    },
  });


  const handleAdd = (itemId) => {
    setLoadingItems((prev) => ({ ...prev, [itemId]: true }));
    setLoadingAction((prev) => ({ ...prev, [itemId]: "add" }));

    addOrUpdateMutation.mutate(
      { restaurantId, ...(reservationId && { reservationId }), itemId },
      {
        // Optimistic update
        onMutate: async ({ itemId }) => {
          await queryClient.cancelQueries(["cart"]);

          const previousCart = queryClient.getQueryData(["cart"]);

          queryClient.setQueryData(["cart"], (old) => {
            if (!old?.data?.items) return old;
            const newItems = old.data.items.map((i) =>
              i.item._id === itemId
                ? { ...i, quantity: i.quantity + 1, itemTotal: (i.itemTotal || 0) + i.item.price }
                : i
            );
            return { ...old, data: { ...old.data, items: newItems } };
          });

          return { previousCart };
        },
        onError: (err, variables, context) => {
          toast.error("Failed to add item");
          queryClient.setQueryData(["cart"], context.previousCart);
        },
        onSettled: () => {
          setLoadingItems((prev) => ({ ...prev, [itemId]: false }));
          setLoadingAction((prev) => {
            const updated = { ...prev };
            delete updated[itemId];
            return updated;
          });
        },
      }
    );
  };


  const handleRemove = (itemId) => {
    setLoadingItems((prev) => ({ ...prev, [itemId]: true }));
    setLoadingAction((prev) => ({ ...prev, [itemId]: "remove" }));

    removeMutation.mutate(
      { restaurantId, ...(reservationId && { reservationId }), itemId },
      {
        onMutate: async ({ itemId }) => {
          await queryClient.cancelQueries(["cart"]);
          const previousCart = queryClient.getQueryData(["cart"]);

          queryClient.setQueryData(["cart"], (old) => {
            if (!old?.data?.items) return old;
            const newItems = old.data.items.map((i) =>
              i.item._id === itemId && i.quantity > 1
                ? { ...i, quantity: i.quantity - 1, itemTotal: (i.itemTotal || 0) - i.item.price }
                : i
            );
            return { ...old, data: { ...old.data, items: newItems } };
          });

          return { previousCart };
        },
        onError: (err, variables, context) => {
          toast.error("Failed to remove item");
          queryClient.setQueryData(["cart"], context.previousCart);
        },
        onSettled: () => {
          setLoadingItems((prev) => ({ ...prev, [itemId]: false }));
          setLoadingAction((prev) => {
            const updated = { ...prev };
            delete updated[itemId];
            return updated;
          });
        },
      }
    );
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedCart(cart);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [cart]);

  // Safety timeout: Reset loading state if stuck for more than 20 seconds
  useEffect(() => {
    if (isGettingLocation) {
      const safetyTimeout = setTimeout(() => {
        console.warn("Location check taking too long, resetting loading state");
        setIsGettingLocation(false);
        toast.error("Location check timed out. Please try again or check your location settings.");
      }, 20000);

      return () => clearTimeout(safetyTimeout);
    }
  }, [isGettingLocation]);

  const getLocationWithRetry = async (maxRetries = 3, retryDelay = 2000) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const position = await Promise.race([
          new Promise((resolve, reject) => {
            let watchId = null;
            let timeoutId = null;

            const cleanup = () => {
              if (watchId !== null) {
                navigator.geolocation.clearWatch(watchId);
                watchId = null;
              }
              if (timeoutId !== null) {
                clearTimeout(timeoutId);
                timeoutId = null;
              }
            };

            watchId = navigator.geolocation.watchPosition(
              (pos) => {
                cleanup();
                resolve(pos);
              },
              (error) => {
                cleanup();
                reject(error);
              },
              {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0
              }
            );

            // Overall timeout wrapper (15 seconds total)
            timeoutId = setTimeout(() => {
              cleanup();
              reject(new Error("Location request timed out. Please check your location settings and try again."));
            }, 15000);
          }),
          // Fallback timeout promise
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error("Location request took too long")), 15000)
          )
        ]);

        return {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        };
      } catch (error) {
        if (attempt === maxRetries) throw error;
       
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
  };

  const checkLocation = async () => {
    try {
    
      const restaurantLocation = cartData?.data?.restaurantLocation;
      

      if (
        !restaurantLocation ||
        !restaurantLocation.coordinates ||
        restaurantLocation.coordinates.length !== 2
      ) {
        throw new Error(t("cart").locationErrors.notAvailable);
      }

      setIsGettingLocation(true);
      let currentLocation = null;

      try {
        // First check if geolocation is supported
        if (!navigator.geolocation) {
          throw new Error("Geolocation is not supported by your browser");
        }

        

        // Try to get location with retries
        currentLocation = await getLocationWithRetry();

        // If successful, save to context
        setUserLocation(currentLocation);

      } catch (error) {
        // If we failed to get new location but have cached location, use that
        if (userLocation) {
        
          currentLocation = userLocation;
        } else {
          // No location available at all
          let errorMessage;
          switch (error.code) {
            case 1: // PERMISSION_DENIED
              errorMessage = "To enable location access:\\n\\n" +
                "For Chrome/Edge:\\n" +
                "1. Click the lock/site settings icon (🔒) in the address bar\\n" +
                "2. Click 'Site settings'\\n" +
                "3. Find 'Location' and change it to 'Allow'\\n" +
                "4. Refresh the page\\n\\n" +
                "For Firefox:\\n" +
                "1. Click the site info button (🛡️) in the address bar\\n" +
                "2. Under 'Permissions', find 'Access Your Location'\\n" +
                "3. Remove the setting or change to 'Allow'\\n" +
                "4. Refresh the page\\n\\n" +
                "For Safari:\\n" +
                "1. Click Safari > Settings > Websites > Location\\n" +
                "2. Find this website and select 'Allow'\\n" +
                "3. Refresh the page";
              break;
            case 2: // POSITION_UNAVAILABLE
              errorMessage = "Cannot get your location. Please check:\\n\\n" +
                "1. Your device's location/GPS is turned ON\\n" +
                "2. You have a working internet connection\\n" +
                "3. You're not in airplane mode\\n" +
                "4. Try using a different browser";
              break;
            case 3: // TIMEOUT
              errorMessage = "Location request took too long. Please:\\n\\n" +
                "1. Check your internet connection\\n" +
                "2. Make sure your GPS is enabled\\n" +
                "3. Try moving to a location with better signal\\n" +
                "4. Refresh and try again";
              break;
            default:
              errorMessage = "Location error: " + error.message + "\\n\\n" +
                "Please try:\\n" +
                "1. Refreshing the page\\n" +
                "2. Using a different browser\\n" +
                "3. Checking your device's location settings";
          }
          throw new Error(errorMessage);
        }
      }

      // Only proceed if we have a location
      if (currentLocation) {
       
        const status = checkUserLocation(restaurantLocation.coordinates, currentLocation);
       
        setLocationStatus(status);
        handleLocationStatus(status);
      } else {
        throw new Error("Could not obtain location");
      }
    } catch (error) {
      console.error("Location check error:", error);
      setLocationError(error.message);

      // Show error in a more prominent way
      toast.error(
        <div>
          <div className="font-bold mb-2">Location Error</div>
          <div style={{ whiteSpace: 'pre-line' }}>{error.message}</div>
        </div>,
        {
          duration: 10000,
          style: {
            maxWidth: '500px',
            padding: '16px',
            fontSize: '14px',
            lineHeight: '1.5'
          }
        }
      );
    } finally {
      setIsGettingLocation(false);
    }
  };

  const handleLocationStatus = (status) => {
    if (status.isInside) {
      setIsTableModalOpen(true);
    } else if (status.canOrder) {
      setIsDeliveryModalOpen(true);
    } else {
      setIsDeliveryModalOpen(true);
      // toast.error(t("cart").locationErrors.tooFar);
    }
  };


  const handleSubmit = () => {
    // If we have a reservation, proceed directly without asking for delivery options
    if (reservationId) {
      const orderData = {
        items: debouncedCart,
        notes: "",
        offerId: selectedOffer?._id,
        restaurantId,
        reservationId
      };
      bookingMutation.mutate(orderData);
      return;
    }

    // For non-reservation orders, handle delivery options
    if (!locationStatus) {
      // If we don't have location status yet, check location
      checkLocation();
    } else if (locationStatus.isInside && !tableNumber) {
      // If user is inside but hasn't entered table number
      setIsTableModalOpen(true);
    } else if (!locationStatus.isInside && !deliveryType) {
      // If user is outside but hasn't selected delivery type
      setIsDeliveryModalOpen(true);
    } else {
      // We have all required info, proceed with order
      const orderData = {
        items: debouncedCart,
        notes: "",
        offerId: selectedOffer?._id,
        restaurantId,
        ...(tableNumber && { tableNumber }),
        ...(deliveryType && { deliveryType }),
        ...(deliveryType === "delivery" && deliveryAddress && { deliveryAddress })
      };
      bookingMutation.mutate(orderData);
    }
  };

  const handleTableSubmit = () => {
    if (!tableNumber.trim()) {
      toast.error(t("cart").enterTableNumber.error);
      return;
    }
    setIsTableModalOpen(false);
    handleSubmit();
  };

  const handleApplyOffer = (offerId) => {
    const selected = offers.find((offer) => offer._id === offerId);

    if (selected) {
      setSelectedOffer(selected);
    }
  };

  const handleGoBack = () => {
    navigate(`/restaurantdetail`, {
      state: { reservationId, restaurantId },
    });
  };
  const { t } = useTranslation();

  if (!cart?.length || !restaurantId) {
    return (
      <div className="lg:py-[100px] md:py-[70px] sm:py-[50px] py-[30px]">
        <div className="container text-center">
          <p className="text-lg font-medium mb-4">{t("cart").emptyMessage}</p>
          <button
            onClick={() => navigate(`/restaurantordering`)}
            className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg text-sm font-medium shadow-sm"
          >
            {t("cart").back}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
    {cardLoadingPrice?<CartLoading/>:<div className="lg:py-[100px] md:py-[70px] sm:py-[50px] py-[30px]">
        <div className="container">
          <div className="grid xl:grid-cols-[669px_1fr] lg:grid-cols-2 grid-cols-1  lg:gap-[54px] gap-[20px]">
            <div className="w-full lg:max-w-[669px] ">
              <div className="mb-4">
                <button
                  // onClick={handleGoBack}
                  onClick={handleGoBack}
                  className="text-green-600 hover:text-green-800 font-medium text-base flex items-center gap-2"
                >
                  {t("cart").backToMenu}
                </button>
              </div>
              <div className="flex lg:gap-[74px] gap-[30px] items-center pb-[28px] border-b border-[#DDDDDD] mb-[28px]">
                <span className="lg:text-lg text-base font-medium leading-[30px] text-[#747474]">
                  {t("cart").steps.cart}
                </span>
                <span className="lg:text-lg text-base font-medium leading-[30px] text-[#747474]">
                  {t("cart").steps.payment}
                </span>
                <span className="lg:text-lg text-base font-medium leading-[30px] text-[#747474]">
                  {t("cart").steps.confirmation}
                </span>
              </div>
              {data?.data?.items?.map((itemObj, index) => {
                const { item, quantity, note, itemTotal } = itemObj;

                return (
                  <div
                    key={item?._id || index}
                    className="p-3 border border-[#DDDDDD] rounded-[10px] shadow-sm mb-[15px] grid md:grid-cols-[auto_1fr] grid-cols-1 gap-[20px] md:justify-between items-center"
                  >
                    <div className="flex gap-[16px] items-center">
                      <img
                        src={
                          item?.images?.length > 0
                            ? `${item?.images[0]}`
                            : CartItem
                        }
                        alt={item?.name || "Menu Item"}
                        className="md:w-[117.1px] md:h-[105px] w-[70px] h-[70px] rounded-[5px] object-cover"
                      />
                      <div>
                        <h3 className="xl:text-lg text-base font-medium leading-[28px] text-black">
                          {item?.itemName || "N/A"}
                        </h3>
                        <p className="xl:text-lg text-base text-[#686868] leading-[28px]">
                          {quantity} item{quantity > 1 ? "s" : ""}
                        </p>
                        {note && (
                          <p className="text-sm text-gray-500 mt-1">
                            {t("cart").note}: {note}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className=" md:text-right text-center">
                      <span className="xl:text-[22px] text-lg font-medium leading-[30px] mb-[8px] inline-block">
                        ₾{itemTotal?.toFixed(2) || "0.00"}
                      </span>
                      <div className="w-full md:max-w-[152px] ml-auto">
                        <div className="flex items-center justify-between border border-gray-300 rounded-full px-4 py-2 md:max-w-[152px]">
                          <button
                            // onClick={() => handleRemoveFromCart(item?._id)}
                            onClick={() => handleRemove(item._id)}
                            className="lg:text-lg text-base px-2 text-gray-700 hover:text-black"
                          >
                            {loadingItems[item._id] && loadingAction[item._id] === "remove" ? (
                              <ClipLoader size={16} color="#4CAF50" />
                            ) : (
                              "–"
                            )}
                          </button>
                          <span className="mx-4 text-base">{quantity}</span>
                          <button
                            onClick={() => handleAdd(item._id)}
                            className="lg:text-lg text-base px-2 text-gray-700 hover:text-black"
                          >
                            {loadingItems[item._id] && loadingAction[item._id] === "add" ? (
                              <ClipLoader size={16} color="#4CAF50" />
                            ) : (
                              "+"
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="w-full lg:max-w-[597px]   h-max  sticky top-[100px]">
              <div className="px-[25px] pt-[25px] pb-[33px] summary-box rounded-[10px]">
                <h2 className="xl:text-[22px] text-lg font-semibold text-black  text-center mb-[30px]">
                  {t("cart").orderSummary}
                </h2>

                {data?.data?.items?.map((itemObj, index) => {
                  const { item, itemTotal } = itemObj;
                  return (
                    <div className="flex justify-between items-center gap-[20px] mb-5 xl:text-lg text-base text-black">
                      <p>{item?.itemName}</p>
                    <span>₾{itemTotal}</span>
                    </div>
                  );
                })}

                {
                  <div className="flex justify-between items-center gap-[20px] mb-5 xl:text-lg text-base text-black">
                    <p>{t("cart").tokenFee}</p>
                    <span>- ₾{charges?.tokenCharges}</span>
                  </div>
                }

                {charges?.discountAmount > 0 && (
                  <div className="flex justify-between items-center gap-[20px] mb-5 xl:text-lg text-base text-black">
                    <p>{t("cart").discount}</p>
                    <span>
                      - ₾{charges?.discountAmount} (
                      {charges?.discountPercentage}%)
                    </span>
                  </div>
                )}

                <div className="flex  justify-between items-center gap-[20px]  xl:text-xl text-lg text-black pt-[19px] font-medium border-t   border-[#DDDDDD] mt-[10px]">
                  <p>{t("cart").totalBeforeTaxes}</p>
                  <span>₾{charges?.netAmount?.toFixed(2)}</span>
                </div>

                {offers?.length > 0 && (
                  <button
                    onClick={() => setIsPopupOpen(true)}
                    className="text-green-600 underline mt-4"
                  >
                    {t("cart").applyDiscount}
                  </button>
                )}
              </div>

              {
                <div className="text-right">
                  <button
                    onClick={handleSubmit}
                    disabled={isGettingLocation}
                    className="inline-block ml-auto rounded-[2px] bg-[#4CAF50] text-[18px] text-white font-semibold py-[19px] px-6 green-shadow hover:bg-[#209b25] md:mt-[35px] mt-[20px] disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isGettingLocation ? (
                      <div className="flex items-center gap-2">
                        <ClipLoader size={16} color="#FFFFFF" />
                        {t("cart").gettingLocation}
                      </div>
                    ) : (
                      t("cart").checkout
                    )}
                  </button>
                </div>
              }
            </div>
          </div>
        </div>
      </div>}
    
      

      <PopupModal isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)}>
        <div className="w-full rounded-[20px] bg-white sm:p-[30px] p-[15px]">
          <h2 className="text-[24px] font-bold mb-[25px] text-gray-800 text-center">
            {t("cart").offersTitle}
          </h2>

          <div className="space-y-6 max-h-[450px] overflow-y-auto px-1">
            {offers?.length > 0 ? (
              offers.map((offer) => (
                <div
                  key={offer._id}
                  className="border border-gray-200 bg-gray-50 rounded-[12px] p-4 flex flex-col sm:flex-row gap-4 shadow-md hover:shadow-lg transition-shadow duration-200"
                >
                  {/* Image */}
                  <img
                    src={`${offer.image}`}
                    alt={offer.title}
                    className="w-full sm:w-[140px] h-[140px] object-cover rounded-[10px] border border-gray-300"
                  />

                  {/* Offer Details */}
                  <div className="flex flex-col justify-between flex-grow">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <h3 className="text-[18px] font-semibold text-gray-900">
                          {offer.title}
                        </h3>
                        <span className="bg-red-500 text-white text-[14px] font-bold px-3 py-1 rounded-full shadow-sm">
                          -{offer.discountPercentage}%
                        </span>
                      </div>
                      <p className="text-[15px] text-gray-600 mt-1">
                        {offer.description}
                      </p>
                    </div>

                    <div className="flex justify-end gap-3 mt-5">
                      {selectedOffer?._id === offer?._id ? (
                        <button
                          className="bg-orange-600 hover:bg-orange-700 text-white px-5 py-2 rounded-lg text-sm font-medium shadow-sm"
                          onClick={() => {
                            setSelectedOffer(null);
                            setIsPopupOpen(false);
                          }}
                        >
                          {t("cart").remove}
                        </button>
                      ) : (
                        <button
                          className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg text-sm font-medium shadow-sm"
                          onClick={() => {
                            launchConfetti();
                            handleApplyOffer(offer._id);
                            setIsPopupOpen(false);
                          }}
                        >
                          {t("cart").apply}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500 text-base">
                {t("cart").noOffers}
              </p>
            )}
          </div>
        </div>
      </PopupModal>

      <PopupModal isOpen={isTableModalOpen} onClose={() => setIsTableModalOpen(false)}>
        <div className="w-full rounded-[20px] bg-white sm:p-[30px] p-[15px]">
          <h2 className="text-[24px] font-bold mb-[25px] text-gray-800 text-center">
            {t("cart").enterTableNumber.title}
          </h2>

          <div className="max-w-sm mx-auto">
            <div className="mb-6">
              <input
                type="text"
                value={tableNumber}
                onChange={(e) => setTableNumber(e.target.value)}
                placeholder={t("cart").enterTableNumber.placeholder}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500 text-lg"
              />
            </div>

            <div className="flex justify-center gap-4">
              <button
                onClick={() => setIsTableModalOpen(false)}
                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
              >
                {t("cart").enterTableNumber.cancel}
              </button>
              <button
                onClick={handleTableSubmit}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                {t("cart").enterTableNumber.confirm}
              </button>
            </div>
          </div>
        </div>
      </PopupModal>

      <PopupModal isOpen={isDeliveryModalOpen} onClose={() => setIsDeliveryModalOpen(false)}>
        <div className="w-full rounded-[20px] bg-white sm:p-[30px] p-[15px]">
          <h2 className="text-[24px] font-bold mb-[25px] text-gray-800 text-center">
            {t("cart").deliveryOptions.title}
          </h2>

          <div className="max-w-sm mx-auto">
            <div className="space-y-4 mb-6">
              <div className="space-y-4">
                <button
                  onClick={() => setDeliveryType("delivery")}
                  className={`w-full px-6 py-4 ${deliveryType === "delivery" ? "bg-green-600" : "bg-gray-500"} text-white rounded-lg hover:bg-green-700 font-medium flex items-center justify-center gap-2`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  {t("cart").deliveryOptions.delivery.title}
                  {locationStatus && <span className="text-sm">({Math.round(locationStatus.distance * 10) / 10}km away)</span>}
                </button>

                {deliveryType === "delivery" && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("cart").deliveryOptions.delivery.address.label}
                    </label>
                    <textarea
                      value={deliveryAddress}
                      onChange={(e) => setDeliveryAddress(e.target.value)}
                      placeholder={t("cart").deliveryOptions.delivery.address.placeholder}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500 text-base min-h-[100px]"
                    />
                  </div>
                )}

                <button
                  onClick={() => setDeliveryType("pickup")}
                  className={`w-full px-6 py-4 ${deliveryType === "pickup" ? "bg-blue-600" : "bg-gray-500"} text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center gap-2`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                  {t("cart").deliveryOptions.pickup.title}
                </button>

                {deliveryType && (
                  <button
                    onClick={() => {
                      if (deliveryType === "delivery" && !deliveryAddress.trim()) {
                        toast.error(t("cart").deliveryOptions.delivery.address.error);
                        return;
                      }
                      setIsDeliveryModalOpen(false);
                      handleSubmit();
                    }}
                    className="w-full px-6 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                  >
                    {t("cart").deliveryOptions.confirm.replace("{type}", deliveryType === "delivery" ? "Delivery" : "Pickup")}
                  </button>
                )}
              </div>
            </div>

            <div className="flex justify-center">
              <button
                onClick={() => setIsDeliveryModalOpen(false)}
                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
              >
                {t("cart").deliveryOptions.cancel}
              </button>
            </div>
          </div>
        </div>
      </PopupModal>
    </>
  );
};

export default CartStep;
